import { enrichFirstFourWithOdds } from "@/utils/early-mail-prices";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLeaderboardQuery } from "@/api/vercel";
import {
  fetchFutureRacesForDate,
  useFutureTipsQuery,
  useEarlyPredictionsQuery,
  useHistoricalResultsQuery,
  useRaceCardsQuery,
} from "@/api/vercel";
import { useQueryClient } from "@tanstack/react-query";
import { useExtraDetails } from "@/hooks/useExtraDetails";
import { filterRacesByCard } from "@/utils/race-cards";
import { formatTimeOfDay, raceDayTipsTime } from "@/utils/time-format";
import { RaceFilterChips, RaceFilterList } from "@/components/RaceFilter";
import {
  addDays,
  format,
  parse,
} from "date-fns";
import { useAbandonedMeetings } from "@/hooks/useAbandonedMeetings";
import { useHotTipSettings } from "@/hooks/useHotTipSettings";
import { usePaidUser } from "@/hooks/usePaidUser";
import { logger } from "@/utils/logger";
import { trackTipHistoryClick } from "@/utils/analytics";
import RenderTips from "@/utils/render-tips";
import { transformRaceData } from "@/utils/transform-race-data";
import { AlertTriangle, ChevronRight, ChevronDown, Frown, ArrowUpDown, ArrowUp, ArrowDown, Mail } from "lucide-react";
import ResultsTally from "@/components/ResultsTally";
import AbandonedLabel from "@/components/abandoned-label";
import CarouselBanner from "@/components/carousel-banner";
import FlameIcon from "@/components/icons/flame-icon";
import { useCarousel } from "@/components/layouts/RacingLayout";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import FutureTipsAccordion from "@/components/FutureTipsAccordion";
import { buildFutureTipsSlots } from "@/utils/future-tips-slots";

function EarlyMailPill({ isOpen }) {
  const [phase, setPhase] = useState("intro"); // intro -> idle -> hover
  const [hoverNonce, setHoverNonce] = useState(0);

  const iconAnimClass =
    phase === "intro"
      ? "early-mail-envelope--intro"
      : phase === "hover"
        ? "early-mail-envelope--hover"
        : "";

  const onIconAnimationEnd = useCallback(() => {
    setPhase("idle");
  }, []);

  const onPillMouseEnter = useCallback(() => {
    setPhase("hover");
    setHoverNonce((n) => n + 1);
  }, []);

  return (
    <span
      onMouseEnter={onPillMouseEnter}
      className="early-mail-pill inline-flex items-center cursor-pointer border border-[#fba6e1] px-[10px] rounded-[10px] shadow-[1px_1px_1px_0px_rgba(0,0,0,0.25)] gap-2 text-[0.7em] font-[800] text-brand-pink uppercase flex-shrink-0 transform origin-center transition-transform duration-150 ease-out hover:scale-[1.15] bg-white"
    >
      <Mail
        aria-hidden="true"
        key={`${phase}-${hoverNonce}`}
        onAnimationEnd={onIconAnimationEnd}
        className={`w-3.5 h-3.5 relative scale-[1.2] top-[0.02em] early-mail-envelope ${iconAnimClass}`}
      />
      <span>{isOpen ? "Close" : "Open"} Early Mail</span>
      {isOpen ? (
        <ChevronDown className="w-3.5 h-3.5 text-brand-pink" aria-hidden="true" />
      ) : (
        <ChevronRight className="w-3.5 h-3.5 text-brand-pink" aria-hidden="true" />
      )}
    </span>
  );
}

export default function HomePage() {
  // Memoize date values to prevent unnecessary re-renders and effect re-runs
  const today = useMemo(() => format(new Date(), "dd-MM-yyyy"), []);
  const queryClient = useQueryClient();
  const raceContainerRef = useRef(null);
  const [selectedRace, setSelectedRace] = useState(null);
  const [nextToJumpRace, setNextToJumpRace] = useState(null);
  const [isNextToJumpActive, setIsNextToJumpActive] = useState(false);
  const [hasRacesToday, setHasRacesToday] = useState(false);
  const { hotTipMinPrice, isLoading: isHotTipSettingsLoading } = useHotTipSettings();
  const isPaidUser = usePaidUser();

  // Results-related state variables
  const [trackResults, setTrackResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedRaceResult, setSelectedRaceResult] = useState(null);
  const [hasHistoricalData, setHasHistoricalData] = useState(false);
  const [isTipHistoryExpanded, setIsTipHistoryExpanded] = useState(false);
  const [trackData, setTrackData] = useState({});
  const [noDataForCurrentPeriod, setNoDataForCurrentPeriod] = useState(false);
  const noDataFlaggedRef = useRef(false);
  const dataProcessedRef = useRef(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [viewMode, setViewMode] = useState("all-tracks"); // all-tracks, track-races, single-race

  // Tips section state variables
  const [tipsTrackResults, setTipsTrackResults] = useState([]);
  const [tipsFilteredResults, setTipsFilteredResults] = useState([]);
  const [selectedTipsTrack, setSelectedTipsTrack] = useState(null);
  const [selectedTipsRace, setSelectedTipsRace] = useState(null);
  const [tipsBreadcrumbs, setTipsBreadcrumbs] = useState([]);
  const [tipsViewMode, setTipsViewMode] = useState("all-tracks");
  const tipsProcessedRef = useRef(null);

  // Add a loading state for the tips section that we can control directly
  const [isTipsLoading, setIsTipsLoading] = useState(false);
  const [isTodaysTipsOpen, setIsTodaysTipsOpen] = useState(true);

  // Add a state to track route changes
  const [hasNavigated, setHasNavigated] = useState(false);

  // Future tips (1-3 days ahead) - using React Query for proper caching
  // Memoize dates to prevent unnecessary query re-creation
  const { date1, date2, date3 } = useMemo(() => {
    const base = new Date();
    return {
      date1: format(addDays(base, 1), "dd-MM-yyyy"),
      date2: format(addDays(base, 2), "dd-MM-yyyy"),
      date3: format(addDays(base, 3), "dd-MM-yyyy"),
    };
  }, []);

  const futureTips1 = useFutureTipsQuery({ date: date1, enabled: isPaidUser });
  const futureTips2 = useFutureTipsQuery({ date: date2, enabled: isPaidUser });
  const futureTips3 = useFutureTipsQuery({ date: date3, enabled: isPaidUser });

  // True while any Early Mail day is still loading (days that are ready show straight away)
  const isFutureTipsLoading = futureTips1.isLoading || futureTips2.isLoading || futureTips3.isLoading;

  // Early Mail accordion visibility (default: closed)
  const [earlyMailOpenByKey, setEarlyMailOpenByKey] = useState({});
  const [pendingScrollToId, setPendingScrollToId] = useState(null);

  const getEarlyMailOpen = useCallback(
    (key) => Boolean(earlyMailOpenByKey?.[key]),
    [earlyMailOpenByKey],
  );

  const toggleEarlyMailOpen = useCallback((key) => {
    setEarlyMailOpenByKey((prev) => ({
      ...(prev || {}),
      [key]: !prev?.[key],
    }));
  }, []);

  // When expanding an Early Mail panel near the bottom of the viewport, scroll it into view
  useEffect(() => {
    if (!pendingScrollToId) return;

    let attempts = 0;
    const maxAttempts = 8;

    const tryScroll = () => {
      attempts += 1;
      const el = document.getElementById(pendingScrollToId);
      if (el) {
        // Scroll so the expanded content becomes visible
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setPendingScrollToId(null);
        return;
      }
      if (attempts < maxAttempts) {
        window.setTimeout(tryScroll, 40);
      } else {
        setPendingScrollToId(null);
      }
    };

    // Wait a tick for the panel to render
    window.requestAnimationFrame(() => window.requestAnimationFrame(tryScroll));
  }, [pendingScrollToId]);

  const toggleTodaysTipsOpen = useCallback(() => {
    setIsTodaysTipsOpen((v) => !v);
  }, []);

  // Early-morning fallback: show "today" using future predictions until normal tips load
  const [futureTodayTracks, setFutureTodayTracks] = useState([]);
  const [isFutureTodayLoading, setIsFutureTodayLoading] = useState(false);

  // Sort toggle state variables
  const [sortType, setSortType] = useState("popularity"); // popularity, a-z, winners, places, both
  const [sortDirection, setSortDirection] = useState("asc"); // asc, desc
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Extra Details: race filter by prize money / Group, from today's race cards
  const { showExtraDetails } = useExtraDetails();
  const [raceFilter, setRaceFilter] = useState("all"); // all, big ($200k+), group
  const { data: raceCardsResponse } = useRaceCardsQuery({
    date: today,
    enabled: showExtraDetails,
  });
  const raceCards = raceCardsResponse?.data;
  const hasRaceCards = !!raceCards && Object.keys(raceCards).length > 0;
  const activeRaceFilter =
    showExtraDetails && hasRaceCards ? raceFilter : "all";
  const raceFilterCounts = useMemo(
    () => ({
      big: filterRacesByCard(tipsFilteredResults, raceCards, "big").length,
      group: filterRacesByCard(tipsFilteredResults, raceCards, "group").length,
    }),
    [tipsFilteredResults, raceCards],
  );
  const filteredCardRaces = useMemo(
    () => filterRacesByCard(tipsFilteredResults, raceCards, activeRaceFilter),
    [tipsFilteredResults, raceCards, activeRaceFilter],
  );

  // Add a ref for the carousel
  const { ref: carouselRef, hasRaces: hasCarouselRaces, isLoading: isCarouselLoading } = useCarousel();
  const {
    data: { data: leaderboardQueryData } = {},
    isError: isLeaderboardError,
    isLoading: isLeaderboardLoading,
  } = useLeaderboardQuery({ date: today, collection: "predictions" });

  // Get historical results data for today
  const {
    data: histResults = { data: {} },
    isError: isHistoricalError,
    isLoading: isHistoricalLoading,
  } = useHistoricalResultsQuery({ date: today, fetchAll: false });

  // Safely get the historical data with a default empty object
  const historicalData = histResults?.data || {};

  // Get early predictions data for today
  const {
    data: earlyPredictions = { data: {} },
    isLoading: isEarlyPredictionsLoading,
  } = useEarlyPredictionsQuery({ date: today, fetchAll: false });

  // Safely get the early predictions data
  const earlyPredictionsData = earlyPredictions?.data || {};

  // Process race data from leaderboardQueryData
  const [races, setRaces] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Add state for abandoned races tracking
  const [abandonedRaces, setAbandonedRaces] = useState({});

  // Get abandoned meetings data
  const { abandonedMeetings, isTrackAbandoned, getAbandonmentReason } =
    useAbandonedMeetings(today);

  // Helper function to get the state for a track from track data
  const getTrackStateFromData = (trackName, currentTrackData = trackData) => {
    if (!trackName) return ""; // Add safety check for undefined trackName

    // Clean up track name to match the format in track-names.json
    // Remove state in parentheses if present
    const cleanTrackName = trackName.replace(/\s*\([^)]*\)\s*$/, "").trim();

    // Try to find the track in the track data
    if (currentTrackData && currentTrackData[cleanTrackName]) {
      return currentTrackData[cleanTrackName].State || "";
    }

    // If not found, try to find a partial match
    if (currentTrackData) {
      const trackKeys = Object.keys(currentTrackData);
      for (const key of trackKeys) {
        if (cleanTrackName.includes(key) || key.includes(cleanTrackName)) {
          return currentTrackData[key].State || "";
        }

        // Check Alt Name as well
        if (
          currentTrackData[key]["alt"] &&
          (cleanTrackName.includes(currentTrackData[key]["alt"]) ||
            currentTrackData[key]["alt"].includes(cleanTrackName))
        ) {
          return currentTrackData[key].State || "";
        }
      }
    }

    // If still not found, extract state from parentheses as fallback
    const stateFromParens = getTrackState(trackName);
    return stateFromParens || "";
  };

  // Extract state from track name (e.g., "Ascot (WA)" -> "WA")
  const getTrackState = (trackName) => {
    const match = trackName.match(/\(([^)]+)\)/);
    return match ? match[1] : "";
  };

  // Toggle the tip history expansion
  const toggleTipHistory = () => {
    const nextState = !isTipHistoryExpanded;
    setIsTipHistoryExpanded(nextState);
    trackTipHistoryClick({
      isExpanded: nextState,
      pageContext: "todays-tips",
      path: `${location.pathname}${location.search}`,
    });
  };

  // Helper to create leaderboard query data from firstFourData
  const createLeaderboardQueryData = (firstFourData) => {
    if (
      !firstFourData ||
      !Array.isArray(firstFourData) ||
      firstFourData.length === 0
    ) {
      return [];
    }
    // Transform the data to the required format
    return transformRaceData(firstFourData);
  };

  // Add function to check for early predictions
  const checkHasEarlyPredictions = (
    track,
    race,
    date,
    leaderboardData,
    earlyData,
  ) => {
    if (!track || !race || !date || !leaderboardData) {
      if (!earlyData || !leaderboardData.earlyPredictionData) {
        return false;
      }
    }

    const raceKey = `${track.replace(/\s+/g, "-")}-${race}-${format(date, "dd-MM-yyyy")}`;

    // Get early predictions for this race
    const earlyPredictionsForRace = earlyData[raceKey]?.firstFourData;

    // Get tipUpdatedAt values for both datasets
    const regularTipUpdatedAt = leaderboardData?.tipUpdatedAt;
    const earlyTipUpdatedAt = earlyData[raceKey]?.tipUpdatedAt;

    if (leaderboardData?.earlyPredictionData?.firstFourData !== "N/A") {
      return true;
    } else if (earlyPredictionsForRace) {
      return true;
    } else {
      return false;
    }
  };

  // Fetch track data from JSON file
  useEffect(() => {
    // Add a flag to track loading state
    let isMounting = true;

    fetch("/data/horseracing/tracks/track-names.json")
      .then((response) => response.json())
      .then((data) => {
        if (isMounting) {
          // logger.log("Track data loaded successfully", Object.keys(data).length);
          setTrackData(data);
          // If we already have results, update their states with the new track data
          if (trackResults.length > 0) {
            const updatedTrackResults = trackResults.map((track) => ({
              ...track,
              state:
                data && track.name
                  ? getTrackStateFromData(track.name, data)
                  : track.state || "",
            }));
            setTrackResults(updatedTrackResults);
            setFilteredResults(updatedTrackResults);
          }

          // Also update tips results with new track data
          if (tipsTrackResults.length > 0) {
            const updatedTipsResults = tipsTrackResults.map((track) => ({
              ...track,
              state:
                data && track.name
                  ? getTrackStateFromData(track.name, data)
                  : track.state || "",
            }));
            setTipsTrackResults(updatedTipsResults);
            setTipsFilteredResults(updatedTipsResults);
          }
        }
      })
      .catch((error) => {
        logger.error("Error loading track data:", error);
      });

    return () => {
      isMounting = false;
    };
  }, [isMounted]); // Add isMounted as a dependency to trigger on navigation

  // Process historical data when it loads (for today only)
  useEffect(() => {
    // State to track current processing cycle
    const currentHistDataId = JSON.stringify({
      dataLength: Object.keys(historicalData || {}).length,
      trackDataLength: Object.keys(trackData).length,
    });

    // Skip if we've already processed exactly this data or if trackData is empty
    if (
      dataProcessedRef.current === currentHistDataId ||
      Object.keys(trackData).length === 0
    ) {
      return;
    }

    // Set the processed flag before any state updates to break potential loops
    dataProcessedRef.current = currentHistDataId;

    // Helper to reset the UI state when no data is available
    const resetUIState = () => {
      setTrackResults([]);
      setFilteredResults([]);
      setIsMounted(false);
    };

    // If no data is available, handle gracefully
    if (!historicalData || Object.keys(historicalData).length === 0) {
      if (!noDataFlaggedRef.current) {
        noDataFlaggedRef.current = true;
        setNoDataForCurrentPeriod(true);
      }
      resetUIState();
      return;
    }

    try {
      const historicalEntries = Object.entries(historicalData);

      if (historicalEntries.length === 0) {
        if (!noDataFlaggedRef.current) {
          noDataFlaggedRef.current = true;
          setNoDataForCurrentPeriod(true);
        }
        resetUIState();
        return;
      }

      // We have data - reset the no data flag if needed
      if (noDataFlaggedRef.current) {
        noDataFlaggedRef.current = false;
        if (noDataForCurrentPeriod) {
          setNoDataForCurrentPeriod(false);
        }
      }

      // Process data into tracks and races
      const newTrackMap = new Map();

      Object.entries(historicalData).forEach(([key, data]) => {
        const parts = key.split("-");
        const day = parts[parts.length - 3];
        const month = parts[parts.length - 2];
        const year = parts[parts.length - 1];
        const raceDate = new Date(year, month - 1, day);

        const raceNumber = parts[parts.length - 4];
        const trackName = parts.slice(0, parts.length - 4).join(" ");

        // Initialize track if not exists
        if (!newTrackMap.has(trackName)) {
          // Make sure to set the state when track is first created
          const trackState = getTrackStateFromData(trackName, trackData);
          // logger.log(`Creating historical track '${trackName}' with state: '${trackState}'`);

          newTrackMap.set(trackName, {
            name: trackName,
            wins: 0,
            places: 0,
            races: new Map(),
            state: trackState || "",
          });
        }

        const track = newTrackMap.get(trackName);

        // Count wins and places
        if (data.podium) {
          data.podium.forEach((result, index) => {
            if (index < 2) {
              if (result === "winnerWinnerChickenDinner") {
                track.wins++;
              } else if (result === "PL") {
                track.places++;
              }
            }
          });
        }

        // Add race data
        if (!track.races.has(raceNumber)) {
          track.races.set(raceNumber, {
            number: raceNumber,
            wins: 0,
            places: 0,
            data: data,
            date: raceDate,
            key: key,
            hasResults:
              data.podium &&
              Array.isArray(data.podium) &&
              data.podium.length > 0,
          });
        }

        const race = track.races.get(raceNumber);

        // Flag race as having results
        if (
          data.podium &&
          Array.isArray(data.podium) &&
          data.podium.length > 0
        ) {
          race.hasResults = true;
        }

        // Count race-specific wins and places
        if (data.podium) {
          data.podium.forEach((result, index) => {
            if (index < 2) {
              if (result === "winnerWinnerChickenDinner") {
                race.wins++;
              } else if (result === "PL") {
                race.places++;
              }
            }
          });
        }
      });

      // Handle no tracks edge case
      if (newTrackMap.size === 0) {
        if (!noDataFlaggedRef.current) {
          noDataFlaggedRef.current = true;
          setNoDataForCurrentPeriod(true);
        }
        resetUIState();
        return;
      }

      // Prepare track data for display
      const tracksArray = Array.from(newTrackMap.values());

      // Convert race maps to arrays and ensure all tracks have a state
      tracksArray.forEach((track) => {
        // Make sure track state is set correctly
        if (!track.state || track.state === "") {
          track.state = getTrackStateFromData(track.name);
        }
        track.races = Array.from(track.races.values()).sort(
          (a, b) => parseInt(a.number) - parseInt(b.number),
        );
      });

      // Sort tracks by popularity and state as defined
      const sortedTracksArray = sortTracksByPopularityAndState(tracksArray);

      // Update track data state at the end
      setTrackResults(sortedTracksArray);
      setFilteredResults(sortedTracksArray);
      setIsMounted(true);
    } catch (error) {
      logger.error("Error processing historical data:", error);
      if (!noDataFlaggedRef.current) {
        noDataFlaggedRef.current = true;
        setNoDataForCurrentPeriod(true);
      }
      resetUIState();
    }
  }, [historicalData, trackData]);

  // Process leaderboard data for tips section
  useEffect(() => {
    // Skip processing if hot tip settings are still loading
    if (isHotTipSettingsLoading) {
      return;
    }
    
    // Reset navigation state when processing new data
    setHasNavigated(false);
    
    // Skip if we're processing the same data again or if trackData is empty
    const currentProcessedId = JSON.stringify({
      dataLength: leaderboardQueryData
        ? Object.keys(leaderboardQueryData).length
        : 0,
      trackDataLength: Object.keys(trackData).length,
      date: today,
      hotTipMinPrice: hotTipMinPrice // Add hotTipMinPrice to the dependency key
    });

    if (
      tipsProcessedRef.current === currentProcessedId ||
      Object.keys(trackData).length === 0
    ) {
      return;
    }

    // Set the processed flag before any state updates to break potential loops
    tipsProcessedRef.current = currentProcessedId;

    // If no data is available, handle gracefully
    if (
      !leaderboardQueryData ||
      Object.keys(leaderboardQueryData).length === 0
    ) {
      setTipsTrackResults([]);
      setTipsFilteredResults([]);
      return;
    }

    try {
      // Process data into tracks and races
      const newTrackMap = new Map();

      Object.entries(leaderboardQueryData).forEach(([key, data]) => {
        const parts = key.split("-");
        const day = parts[parts.length - 3];
        const month = parts[parts.length - 2];
        const year = parts[parts.length - 1];
        const raceDate = new Date(year, month - 1, day);

        const raceNumber = parts[parts.length - 4];
        const trackName = parts.slice(0, parts.length - 4).join(" ");

        // Check if this is a top tip using the current hotTipMinPrice
        const isTopTip = hotTipMinPrice > 0 && 
                         data?.firstFourData?.length > 0 && 
                         data.firstFourData[0]?.currentPrice && 
                         parseFloat(data.firstFourData[0].currentPrice) > hotTipMinPrice;

        // Initialize track if not exists
        if (!newTrackMap.has(trackName)) {
          const trackState = getTrackStateFromData(trackName, trackData);

          newTrackMap.set(trackName, {
            name: trackName,
            races: new Map(),
            state: trackState || "",
            topTips: isTopTip ? 1 : 0,  // Initialize topTips counter
          });
        } else if (isTopTip) {
          // Update top tips count for existing track
          const track = newTrackMap.get(trackName);
          track.topTips = (track.topTips || 0) + 1;
        }

        const track = newTrackMap.get(trackName);

        // Add race data
        if (!track.races.has(raceNumber)) {
          track.races.set(raceNumber, {
            number: raceNumber,
            data: data,
            date: raceDate,
            key: key,
            isTopTip: isTopTip,  // Add isTopTip flag
          });
        }
      });

      // Handle no tracks edge case
      if (newTrackMap.size === 0) {
        setTipsTrackResults([]);
        setTipsFilteredResults([]);
        return;
      }

      // Prepare track data for display
      const tracksArray = Array.from(newTrackMap.values());

      // Convert race maps to arrays and ensure all tracks have a state
      tracksArray.forEach((track) => {
        if (!track.state || track.state === "") {
          track.state = getTrackStateFromData(track.name);
        }
        track.races = Array.from(track.races.values()).sort(
          (a, b) => parseInt(a.number) - parseInt(b.number),
        );
      });

      // Sort tracks by popularity and state as defined
      const sortedTracksArray = sortTracksByPopularityAndState(tracksArray);

      // Update track data state at the end
      setTipsTrackResults(sortedTracksArray);
      setTipsFilteredResults(sortedTracksArray);
    } catch (error) {
      logger.error("Error processing prediction data:", error);
      setTipsTrackResults([]);
      setTipsFilteredResults([]);
    }
  }, [leaderboardQueryData, trackData, today, hotTipMinPrice, isHotTipSettingsLoading]);

  // ==================
  // Future tips section (1-3 days into the future)
  // ==================

  const extractFutureTipsPayload = (responseJson) => {
    // Backend wrappers vary; reliably find the first object that "looks like" the payload:
    // { date, trackCount, tracks: [...] }
    const seen = new Set();
    const looksLikeTracksPayload = (obj) => {
      if (!obj || typeof obj !== "object") return false;
      if (!Array.isArray(obj.tracks)) return false;
      // We specifically want: tracks: [{ trackName, races: [...] }, ...]
      // Avoid accidentally matching other arrays like metadata.tracks (strings).
      const first = obj.tracks[0];
      if (!first || typeof first !== "object") return false;
      if (Array.isArray(first.races)) return true;
      if (typeof first.trackName === "string" || typeof first.track === "string")
        return true;
      return false;
    };
    const walk = (value, depth = 0) => {
      if (!value || typeof value !== "object") return null;
      if (seen.has(value)) return null;
      seen.add(value);
      if (depth > 6) return null;

      if (looksLikeTracksPayload(value)) return value;
      if (value.data) return walk(value.data, depth + 1);
      if (value.result) return walk(value.result, depth + 1);
      if (value.payload) return walk(value.payload, depth + 1);

      // fallback: scan one level deep for a tracks-bearing object
      for (const v of Object.values(value)) {
        const found = walk(v, depth + 1);
        if (found) return found;
      }
      return null;
    };

    return walk(responseJson) || {};
  };

  const normalizeTrackKeyPart = (trackName) =>
    `${trackName || ""}`.trim().replace(/\s+/g, "-");

  const normalizeTrackDisplayName = (trackName) =>
    `${trackName || ""}`.trim().replace(/-/g, " ");

  const buildTracksArrayFromFuturePayload = (payload, dateStr) => {
    const tracks = Array.isArray(payload?.tracks) ? payload.tracks : [];
    if (!tracks.length) return [];

    const newTrackMap = new Map();
    const raceDateObj = parse(dateStr, "dd-MM-yyyy", new Date());

    tracks.forEach((t) => {
      const rawTrackName = t?.trackName || t?.track || "";
      const trackDisplayName = normalizeTrackDisplayName(rawTrackName);
      const trackKeyPart = normalizeTrackKeyPart(rawTrackName);

      const races = Array.isArray(t?.races) ? t.races : [];
      if (!races.length) return;

      if (!newTrackMap.has(trackDisplayName)) {
        const trackState = getTrackStateFromData(trackDisplayName, trackData);
        newTrackMap.set(trackDisplayName, {
          name: trackDisplayName,
          races: new Map(),
          state: trackState || "",
          topTips: 0,
        });
      }

      const trackEntry = newTrackMap.get(trackDisplayName);

      races.forEach((r) => {
        const raceNumberRaw = r?.race ?? r?.raceNumber ?? r?.number ?? r?.id;
        const raceNumber = `${raceNumberRaw || ""}`.toString();
        if (!raceNumber) return;

        const raceKey = `${trackKeyPart}-${raceNumber}-${dateStr}`;

        // Ensure required fields exist for downstream display utilities
        const normalizedRaceData = enrichFirstFourWithOdds({
          ...r,
          track: trackDisplayName,
          race: parseInt(raceNumber, 10) || raceNumber,
          date: dateStr,
        });

        if (!trackEntry.races.has(raceNumber)) {
          trackEntry.races.set(raceNumber, {
            number: raceNumber,
            data: normalizedRaceData,
            date: raceDateObj,
            key: raceKey,
            isTopTip: false,
          });
        }
      });
    });

    const tracksArray = Array.from(newTrackMap.values());
    tracksArray.forEach((track) => {
      if (!track.state || track.state === "") {
        track.state = getTrackStateFromData(track.name, trackData) || "";
      }
      track.races = Array.from(track.races.values()).sort(
        (a, b) => parseInt(a.number) - parseInt(b.number),
      );
    });

    return sortTracksByPopularityAndState(tracksArray);
  };

  // Effect to detect route changes
  useEffect(() => {
    // Track when we navigate to this component
    setHasNavigated(true);
    
    // Show brief loading state when navigating between routes
    setIsTipsLoading(true);
    
    // If we have data already, don't show loading state for long
    const timer = setTimeout(() => {
      if (tipsFilteredResults.length > 0) {
        setIsTipsLoading(false);
      }
    }, 300);
    
    // Return cleanup function to reset tips processed ref when we navigate away
    return () => {
      clearTimeout(timer);
      // Reset the tips processed ref when component unmounts
      tipsProcessedRef.current = null;
    };
  }, [location.pathname]);

  // Effect to update loading state when tips data changes
  useEffect(() => {
    // If we have tips data, we're not loading
    if (tipsFilteredResults.length > 0) {
      setIsTipsLoading(false);
    }
  }, [tipsFilteredResults]);

  // ==================
  // Tips section functions
  // ==================

  // Handle tips track selection
  const selectTipsTrack = (track) => {
    setSelectedTipsTrack(track);
    setSelectedTipsRace(null);
    setTipsViewMode("track-races");
    setTipsBreadcrumbs([
      {
        name: "All Tracks",
        onClick: () => handleTipsBreadcrumbClick({ path: "home" }),
      },
      { name: track.name, path: null },
    ]);
  };

  // Handle tips race selection
  const selectTipsRace = (track, race) => {
    // Replace spaces with hyphens in track name and then encode for URL
    const formattedTrackName = track.name.replace(/\s+/g, "-").toLowerCase();

    // Use client-side navigation instead of full page reload
    if (
      window.clientSideRouter &&
      typeof window.clientSideRouter.navigate === "function"
    ) {
      // If using React Router
      window.clientSideRouter.navigate(
        `/todays-tips?track=${formattedTrackName}&race=${race.number}`,
      );
      
      // Dispatch navigationend event after navigation
      setTimeout(() => window.dispatchEvent(new CustomEvent("navigationend")), 50);
    } else {
      // Fallback: update URL without reloading page and dispatch a custom event
      const url = new URL(window.location.origin + `/todays-tips`);
      url.searchParams.set("track", formattedTrackName);
      url.searchParams.set("race", race.number);
      window.history.pushState({}, "", url);

      // Dispatch custom event to notify the app of the navigation
      window.dispatchEvent(
        new CustomEvent("urlchange", {
          detail: {
            pathname: "/todays-tips",
            search: `?track=${formattedTrackName}&race=${race.number}`,
            track: formattedTrackName,
            race: race.number,
          },
        }),
      );
      
      // Dispatch navigationend event
      window.dispatchEvent(new CustomEvent("navigationend"));

      // Navigate to the new page
      window.location.href = url.toString();
    }
  };

  // Handle breadcrumb navigation for tips section
  const handleTipsBreadcrumbClick = (crumb) => {
    // logger.log("handleTipsBreadcrumbClick: crumb = ", crumb);
    if (crumb.path === "home" || crumb.name === "All Tracks") {
      setSelectedTipsTrack(null);
      setSelectedTipsRace(null);
      setTipsViewMode("all-tracks");
      setTipsBreadcrumbs([]);
    } else if (crumb.trackName || crumb.name) {
      // Find the track by name
      // logger.log("tipsFilteredResults = ", tipsFilteredResults);
      const track = tipsFilteredResults.find(
        (t) => t.name === crumb.trackName || t.name === crumb.name,
      );
      if (track) {
        setSelectedTipsTrack(track);
        setSelectedTipsRace(null);
        setTipsViewMode("track-races");
        setTipsBreadcrumbs([
          {
            name: "All Tracks",
            onClick: () => handleTipsBreadcrumbClick({ path: "home" }),
          },
          { name: track.name, path: null },
        ]);
      }
    }
  };

  // ==================
  // Results section functions
  // ==================

  // Handle track selection
  const selectTrack = (track) => {
    setSelectedTrack(track);
    setSelectedRaceResult(null);
    setBreadcrumbs([
      { name: "All Tracks", path: "home" },
      { name: track.name, path: null },
    ]);
  };

  // Handle race selection
  const selectRace = (track, race) => {
    setSelectedTrack(track);
    setSelectedRaceResult(race);
    setBreadcrumbs([
      { name: "All Tracks", path: "home" },
      {
        name: track.name,
        path: null,
        onClick: () => selectTrack(track),
      },
      { name: `Race ${race.number}`, path: null },
    ]);
  };

  // Helper function to sort tracks by popularity and state
  const sortTracksByPopularityAndState = (tracksArray) => {
    // Define an ordering for states
    const stateOrder = {
      VIC: 1,
      NSW: 2,
      QLD: 3,
      SA: 4,
      WA: 5,
      TAS: 6,
      Other: 7,
    };

    // Define popular metro tracks to prioritize - flattened list with global priority
    const popularMetroTracks = {
      VIC: ["Flemington", "Caulfield", "Moonee Valley", "Sandown", "Sandown Lakeside", "Sandown Hillside"],
      NSW: ["Randwick", "Rosehill", "Kensington", "Canterbury", "Warwick Farm"],
      QLD: ["Eagle Farm", "Doomben"],
      SA: ["Morphettville", "Morphettville Parks", "Balaklava"],
      WA: ["Ascot", "Belmont"],
      TAS: ["Hobart", "Launceston"],
    };

    // Create a global priority list for popular metro tracks
    const globalMetroPriority = [];
    Object.keys(stateOrder).forEach(state => {
      if (popularMetroTracks[state]) {
        popularMetroTracks[state].forEach(track => {
          globalMetroPriority.push(track);
        });
      }
    });

    // Helper to check if a track is metro
    const isMetroTrack = (track) => {
      const state = track.state || "Other";
      const metroList = popularMetroTracks[state] || [];
      return metroList.some(
        (metroTrack) =>
          track.name === metroTrack ||
          // Handle variations in Moonee Valley naming
          (metroTrack === "Moonee Valley" && track.name.includes("Moonee")),
      );
    };

    // Helper to get global metro priority
    const getGlobalMetroPriority = (track) => {
      // Find exact match first
      const exactIndex = globalMetroPriority.findIndex((m) => track.name === m);
      if (exactIndex !== -1) return exactIndex;

      // Handle Moonee Valley variations
      if (track.name.includes("Moonee")) {
        const mooneeIndex = globalMetroPriority.findIndex((m) => m === "Moonee Valley");
        if (mooneeIndex !== -1) return mooneeIndex;
      }

      return globalMetroPriority.length; // Put at the end if not found
    };

    // Sort the tracks: popular metro tracks first (across all states), then by state order
    return tracksArray.sort((a, b) => {
      const isMetroA = isMetroTrack(a);
      const isMetroB = isMetroTrack(b);

      // If both are metro tracks, sort by their global priority
      if (isMetroA && isMetroB) {
        return getGlobalMetroPriority(a) - getGlobalMetroPriority(b);
      }

      // Metro tracks come before non-metro tracks
      if (isMetroA && !isMetroB) return -1;
      if (!isMetroA && isMetroB) return 1;

      // For non-metro tracks, sort by state first, then alphabetically
      const stateA = a.state || "Other";
      const stateB = b.state || "Other";

      if (stateA !== stateB) {
        return stateOrder[stateA] - stateOrder[stateB];
      }

      // Within the same state for non-metro tracks, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  // Compute future tips data from React Query results using useMemo for stability
  const futureTipsDays = useMemo(() => {
    if (!isPaidUser) {
      return [];
    }

    // Process query results into the expected format
    const queries = [
      { data: futureTips1.data, isSuccess: futureTips1.isSuccess, date: date1 },
      { data: futureTips2.data, isSuccess: futureTips2.isSuccess, date: date2 },
      { data: futureTips3.data, isSuccess: futureTips3.isSuccess, date: date3 },
    ];

    const days = queries
      .filter(({ isSuccess, data }) => isSuccess && data)
      .map(({ data, date }) => {
        const payload = extractFutureTipsPayload(data);
        const tracksArray = buildTracksArrayFromFuturePayload(payload, date);
        return { date, tracks: tracksArray };
      })
      .filter((day) => Array.isArray(day.tracks) && day.tracks.length > 0);

    // Apply track state data when available
    if (Object.keys(trackData).length > 0) {
      return days.map((day) => ({
        ...day,
        tracks: sortTracksByPopularityAndState(
          (day.tracks || []).map((t) => ({
            ...t,
            state: getTrackStateFromData(t.name, trackData) || t.state || "",
          })),
        ),
      }));
    }

    return days;
  }, [isPaidUser, futureTips1.data, futureTips2.data, futureTips3.data, futureTips1.isSuccess, futureTips2.isSuccess, futureTips3.isSuccess, date1, date2, date3, trackData]);

  // Early Mail rows in date order: a ready day shows at once, a slow day keeps its place with a loading line
  const futureTipsSlots = useMemo(
    () =>
      isPaidUser
        ? buildFutureTipsSlots(
            [
              { date: date1, isLoading: futureTips1.isLoading },
              { date: date2, isLoading: futureTips2.isLoading },
              { date: date3, isLoading: futureTips3.isLoading },
            ],
            futureTipsDays,
          )
        : [],
    [isPaidUser, date1, date2, date3, futureTips1.isLoading, futureTips2.isLoading, futureTips3.isLoading, futureTipsDays],
  );

  // Get back to all tracks view
  const backToAllTracks = () => {
    setSelectedTrack(null);
    setSelectedRaceResult(null);
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (breadcrumb) => {
    if (breadcrumb.onClick) {
      breadcrumb.onClick();
    } else if (breadcrumb.path === "home") {
      // Navigate to home (all tracks)
      setSelectedTrack(null);
      setSelectedRaceResult(null);
      setBreadcrumbs([]);
    }
  };

  // Track data loading effect - only runs once on mount
  const trackDataLoadedRef = useRef(false);
  useEffect(() => {
    // Only load track data once if not already loaded
    if (!trackDataLoadedRef.current && Object.keys(trackData).length === 0) {
      trackDataLoadedRef.current = true;
      fetch("/data/horseracing/tracks/track-names.json")
        .then((response) => response.json())
        .then((data) => {
          setTrackData(data);
        })
        .catch((error) => {
          logger.error("Error loading track data:", error);
        });
    }
  }, [trackData]);

  // Process race data from leaderboardQueryData
  useEffect(() => {
    if (leaderboardQueryData && Object.keys(leaderboardQueryData).length > 0) {
      try {
        const currentDate = new Date();
        // Process races as before

        // Check if URL parameters are missing and redirect to an appropriate race
        const url = new URL(window.location.href);
        const trackParam = url.searchParams.get("track");
        const raceParam = url.searchParams.get("race");

        // ONLY attempt redirection if we're on the todays-tips page and have NO parameters
        if (
          window.location.pathname === "/todays-tips" &&
          !trackParam &&
          !raceParam &&
          races.length > 0
        ) {
          let redirectRace;

          // First try to get the next race to jump
          redirectRace = races.find((race) => !race.isCompleted);

          // If all races are completed, use the last chronological race
          if (!redirectRace) {
            // Sort by time and get the last race of the day
            const chronologicalRaces = [...races].sort(
              (a, b) => a.marketStartTime - b.marketStartTime,
            );
            redirectRace = chronologicalRaces[chronologicalRaces.length - 1];
          }

          if (redirectRace) {
            // Format track name for URL
            const formattedTrackName = redirectRace.track
              .replace(/\s+/g, "-")
              .toLowerCase();
            const formattedRaceNumber = redirectRace.raceNumber.toString();

            // Update URL without page reload
            const newUrl = new URL(window.location.origin + `/todays-tips`);
            newUrl.searchParams.set("track", formattedTrackName);
            newUrl.searchParams.set("race", formattedRaceNumber);

            window.history.pushState({}, "", newUrl);

            // Dispatch custom event to notify components of the URL change
            window.dispatchEvent(
              new CustomEvent("urlchange", {
                detail: {
                  pathname: "/todays-tips",
                  search: `?track=${formattedTrackName}&race=${formattedRaceNumber}`,
                  track: formattedTrackName,
                  race: formattedRaceNumber,
                },
              }),
            );

            logger.log(
              `No track/race specified, defaulted to ${redirectRace.track} R${redirectRace.raceNumber}`,
            );
          }
        }

        // Original code continues...
      } catch (error) {
        logger.error("Error processing race data:", error);
        // Original error handling...
      }
    }
  }, [leaderboardQueryData, races]);

  // Add a similar check on component mount to handle direct page loads
  useEffect(() => {
    // Check URL parameters on load
    const checkURLParameters = () => {
      if (window.location.pathname === "/todays-tips") {
        const url = new URL(window.location.href);
        const trackParam = url.searchParams.get("track");
        const raceParam = url.searchParams.get("race");

        // ONLY process redirect if parameters are completely missing - NEVER override existing parameters
        if ((!trackParam || !raceParam) && races.length > 0) {
          // Double check - if we have at least one parameter, respect it and don't override
          if (trackParam || raceParam) {
            logger.log("Partial parameters exist - keeping existing values");
            return;
          }

          let redirectRace;

          // First try to get the next race to jump
          redirectRace = races.find((race) => !race.isCompleted);

          // If all races are completed, use the last chronological race
          if (!redirectRace) {
            // Sort by time and get the last race of the day
            const chronologicalRaces = [...races].sort(
              (a, b) => a.marketStartTime - b.marketStartTime,
            );
            redirectRace = chronologicalRaces[chronologicalRaces.length - 1];
          }

          if (redirectRace) {
            // Format track name for URL
            const formattedTrackName = redirectRace.track
              .replace(/\s+/g, "-")
              .toLowerCase();
            const formattedRaceNumber = redirectRace.raceNumber.toString();

            // Update URL without page reload
            const newUrl = new URL(window.location.origin + `/todays-tips`);
            newUrl.searchParams.set("track", formattedTrackName);
            newUrl.searchParams.set("race", formattedRaceNumber);

            window.history.pushState({}, "", newUrl);

            // Dispatch custom event to notify components of the URL change
            window.dispatchEvent(
              new CustomEvent("urlchange", {
                detail: {
                  pathname: "/todays-tips",
                  search: `?track=${formattedTrackName}&race=${formattedRaceNumber}`,
                  track: formattedTrackName,
                  race: formattedRaceNumber,
                },
              }),
            );

            logger.log(
              `Initial load with no track/race specified, defaulted to ${redirectRace.track} R${redirectRace.raceNumber}`,
            );
          }
        }
      }
    };

    // Check parameters on mount and when races change
    checkURLParameters();

    // Also listen for URL changes
    window.addEventListener("popstate", checkURLParameters);

    return () => {
      window.removeEventListener("popstate", checkURLParameters);
    };
  }, [races]);

  // Modify the handleDirectNavigation function to manage loading state
  useEffect(() => {
    // Handle direct navigation to /todays-tips without parameters
    const handleDirectNavigation = () => {
      // Only run on the todays-tips route
      if (window.location.pathname !== "/todays-tips") return;

      const url = new URL(window.location.href);
      const trackParam = url.searchParams.get("track");
      const raceParam = url.searchParams.get("race");

      // If we have both parameters, no need to redirect - IMPORTANT: always respect existing parameters
      if (trackParam && raceParam) {
        // Ensure we're not showing loading state when params exist
        setIsTipsLoading(false);
        return;
      }

      // From this point on, we only run if there are no URL parameters

      // Set loading state when navigation starts
      setIsTipsLoading(true);

      // Set a flag to indicate we're handling this to prevent loops
      if (window.__handlingDirectNavigation) return;
      window.__handlingDirectNavigation = true;

      // Wait for races data to be available
      const checkRacesAndRedirect = () => {
        // Only run if track and race params are still missing from URL
        // (double check in case they appeared from elsewhere)
        const currentUrl = new URL(window.location.href);
        const currentTrackParam = currentUrl.searchParams.get("track");
        const currentRaceParam = currentUrl.searchParams.get("race");

        // If parameters appeared since we started, respect them and stop redirect
        if (currentTrackParam && currentRaceParam) {
          setIsTipsLoading(false);
          window.__handlingDirectNavigation = false;
          return true;
        }

        // Use tipsTrackResults as the source of truth for available races
        if (tipsTrackResults.length > 0) {
          // Get all races across all tracks
          const allRaces = [];
          tipsTrackResults.forEach((track) => {
            track.races.forEach((race) => {
              allRaces.push({
                track: track.name,
                raceNumber: race.number,
                marketStartTime: race.data.marketStartTime
                  ? new Date(race.data.marketStartTime)
                  : new Date(),
                isCompleted: race.data.marketStartTime
                  ? new Date(race.data.marketStartTime) < new Date()
                  : false,
              });
            });
          });

          // Sort races by time
          allRaces.sort((a, b) => a.marketStartTime - b.marketStartTime);

          // Find the next race to jump (first non-completed race)
          let redirectRace = allRaces.find((race) => !race.isCompleted);

          // If all races are completed, use the last race
          if (!redirectRace && allRaces.length > 0) {
            redirectRace = allRaces[allRaces.length - 1];
          }

          if (redirectRace) {
            // Format track name for URL
            const formattedTrackName = redirectRace.track
              .replace(/\s+/g, "-")
              .toLowerCase();
            const formattedRaceNumber = redirectRace.raceNumber.toString();

            // Update the URL without reloading
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.set("track", formattedTrackName);
            newUrl.searchParams.set("race", formattedRaceNumber);

            window.history.replaceState({}, "", newUrl);

            // Dispatch custom event to notify components about the URL change
            window.dispatchEvent(
              new CustomEvent("urlchange", {
                detail: {
                  pathname: "/todays-tips",
                  search: `?track=${formattedTrackName}&race=${formattedRaceNumber}`,
                  track: formattedTrackName,
                  race: formattedRaceNumber,
                },
              }),
            );

            logger.log(
              `Direct navigation handled: Redirected to ${redirectRace.track} R${redirectRace.raceNumber}`,
            );

            // Set loading to false after redirection completes
            setIsTipsLoading(false);

            // Clear the handling flag
            setTimeout(() => {
              window.__handlingDirectNavigation = false;
            }, 500);

            return true;
          }
        }

        return false;
      };

      // Try immediately
      if (!checkRacesAndRedirect()) {
        // If not successful, try again after the data loads
        const interval = setInterval(() => {
          if (checkRacesAndRedirect()) {
            clearInterval(interval);
          }
        }, 200);

        // Don't let it run forever
        setTimeout(() => {
          clearInterval(interval);
          window.__handlingDirectNavigation = false;

          // Ensure loading state is turned off even if redirection fails
          setIsTipsLoading(false);

          // ONLY set fallback if we still don't have URL parameters
          const finalCheck = new URL(window.location.href);
          const finalTrackParam = finalCheck.searchParams.get("track");
          const finalRaceParam = finalCheck.searchParams.get("race");

          // If parameters are still missing, use fallback
          if (
            (!finalTrackParam || !finalRaceParam) &&
            tipsTrackResults.length > 0
          ) {
            const firstTrack = tipsTrackResults[0];
            if (firstTrack && firstTrack.races && firstTrack.races.length > 0) {
              const firstRace = firstTrack.races[0];

              // Format track name for URL
              const formattedTrackName = firstTrack.name
                .replace(/\s+/g, "-")
                .toLowerCase();
              const formattedRaceNumber = firstRace.number.toString();

              // Update URL without reloading page
              const newUrl = new URL(window.location.href);
              newUrl.searchParams.set("track", formattedTrackName);
              newUrl.searchParams.set("race", formattedRaceNumber);

              window.history.replaceState({}, "", newUrl);

              // Dispatch custom event
              window.dispatchEvent(
                new CustomEvent("urlchange", {
                  detail: {
                    pathname: "/todays-tips",
                    search: `?track=${formattedTrackName}&race=${formattedRaceNumber}`,
                    track: formattedTrackName,
                    race: formattedRaceNumber,
                  },
                }),
              );

              logger.log(
                `Fallback navigation: Selected first available race ${firstTrack.name} R${firstRace.number}`,
              );
            }
          }
        }, 5000);
      }
    };

    // Run once on component mount
    handleDirectNavigation();

    // Also listen for popstate events (browser back/forward)
    const handlePopState = () => {
      handleDirectNavigation();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [tipsTrackResults]);

  // Add a timeout to automatically hide the loading spinner after a certain period
  useEffect(() => {
    let loadingTimeout;

    if (isTipsLoading) {
      // Set a maximum time the loading spinner can show (10 seconds)
      loadingTimeout = setTimeout(() => {
        setIsTipsLoading(false);
        logger.log("Loading timeout reached, forcing loading state to false");
      }, 10000);
    }

    return () => {
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };
  }, [isTipsLoading]);

  // Use shared carousel data when available
  useEffect(() => {
    if (hasCarouselRaces !== undefined) {
      setHasRacesToday(hasCarouselRaces);
    }
  }, [hasCarouselRaces]);

  // RACE SECTION - add check for abandoned races
  const isRaceAbandoned = useCallback(
    (track, race) => {
      // First check if the entire track is abandoned
      if (isTrackAbandoned(track)) {
        return true;
      }

      // Then check if individual race is abandoned
      const raceKey = `${track.replace(/\s+/g, "-")}-${race}-${today}`;
      const raceData = leaderboardQueryData?.[raceKey];

      return (
        raceData?.isAbandoned === true ||
        raceData?.marketStatus === "ABANDONED" ||
        (raceData?.marketStatus && raceData?.marketStatus.includes("ABANDONED"))
      );
    },
    [isTrackAbandoned, leaderboardQueryData, today],
  );

  // Helper function to check if current time is between midnight and 10:00 AM
  const isEarlyMorning = () => {
    const now = new Date();
    const currentHour = now.getHours();
    return currentHour >= 0 && currentHour < 10;
  };

  // Helper function to check if tips data is from yesterday
  const areTipsFromYesterday = () => {
    if (tipsFilteredResults.length === 0) return false;
    
    // Get the first race's date to check
    const firstTrack = tipsFilteredResults[0];
    if (!firstTrack || !firstTrack.races || firstTrack.races.length === 0) return false;
    
    const firstRace = firstTrack.races[0];
    if (!firstRace || !firstRace.data || !firstRace.data.date) return false;
    
    // Parse the date from the tips data (format: "23-06-2025")
    const tipsDateStr = firstRace.data.date;
    const [day, month, year] = tipsDateStr.split('-');
    const tipsDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    
    // Get today's date
    const today = new Date();
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    // Get yesterday's date
    const yesterday = new Date(todayDateOnly);
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Compare dates
    const tipsDateOnly = new Date(tipsDate.getFullYear(), tipsDate.getMonth(), tipsDate.getDate());
    
    return tipsDateOnly.getTime() === yesterday.getTime();
  };

  // Helper function to determine if we should show the early morning message
  const shouldShowEarlyMorningMessage = () => {
    return isEarlyMorning() && (tipsFilteredResults.length === 0 || areTipsFromYesterday());
  };

  // Fetch today's races via the "future tips" endpoint during early morning,
  // so we can show a preview list until the real tips publish.
  const futureTodayFetchedRef = useRef(false);
  const showEarlyMessage = shouldShowEarlyMorningMessage();
  
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // Don't fetch if we shouldn't show early morning message
      if (!showEarlyMessage) {
        setFutureTodayTracks([]);
        futureTodayFetchedRef.current = false;
        return;
      }

      // Don't re-fetch if we already have data for today
      if (futureTodayFetchedRef.current && futureTodayTracks.length > 0) {
        return;
      }

      try {
        setIsFutureTodayLoading(true);

        // Use queryClient to fetch data (benefits from cache)
        const json = await queryClient.fetchQuery({
          queryKey: ["future-tips", today],
          queryFn: () => fetchFutureRacesForDate(today),
          staleTime: 10 * 60 * 1000, // 10 minutes
        });

        const payload = extractFutureTipsPayload(json);
        const tracksArray = buildTracksArrayFromFuturePayload(payload, today);

        if (!cancelled) {
          setFutureTodayTracks(tracksArray);
          futureTodayFetchedRef.current = true;
        }
      } catch (e) {
        logger.error("Error fetching future-today fallback tips:", e);
        if (!cancelled) setFutureTodayTracks([]);
      } finally {
        if (!cancelled) setIsFutureTodayLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [today, showEarlyMessage, queryClient]);

  // Sort toggle functions
  const getTrackWinsAndPlaces = (trackName) => {
    // Use the same logic as ResultsTally component
    const tally = {
      wins: 0,
      places: 0
    };

    // Process each race in the historical data
    Object.entries(historicalData).forEach(([key, raceData]) => {
      // Skip if no podium data
      if (!raceData.podium || !Array.isArray(raceData.podium)) {
        return;
      }

      // Extract track name from key using same logic as tally-results.js
      const parts = key.split("-");
      const keyTrackName = parts.slice(0, parts.length - 4).join(" ");
      
      // Only process if this key belongs to the track we're looking for
      if (keyTrackName !== trackName) {
        return;
      }

      // Count wins and places for first 2 tips (tipCount = 2)
      raceData.podium.slice(0, 2).forEach((result) => {
        if (result === "winnerWinnerChickenDinner") {
          tally.wins++;
        } else if (result === "PL") {
          tally.places++;
        }
      });
    });

    return tally;
  };

  const sortTracks = (tracks, type, direction) => {
    // logger.log(`🔄 Sorting tracks by ${type} (${direction})`, { tracksCount: tracks.length });
    
    const sorted = [...tracks].sort((a, b) => {
      let comparison = 0;

      switch (type) {
        case "popularity":
          // For popularity, we keep the original order (no sorting needed)
          return 0;
        
        case "a-z":
          comparison = a.name.localeCompare(b.name);
          break;
        
        case "winners":
          const aWins = getTrackWinsAndPlaces(a.name).wins;
          const bWins = getTrackWinsAndPlaces(b.name).wins;
          comparison = aWins - bWins; // Lower wins first by default (asc), will be flipped for desc
          break;
        
        case "places":
          const aPlaces = getTrackWinsAndPlaces(a.name).places;
          const bPlaces = getTrackWinsAndPlaces(b.name).places;
          comparison = aPlaces - bPlaces; // Lower places first by default (asc), will be flipped for desc
          break;
        
        case "both":
          const aTotal = getTrackWinsAndPlaces(a.name).wins + getTrackWinsAndPlaces(a.name).places;
          const bTotal = getTrackWinsAndPlaces(b.name).wins + getTrackWinsAndPlaces(b.name).places;
          comparison = aTotal - bTotal; // Lower total first by default (asc), will be flipped for desc
          break;
        
        default:
          return 0;
      }

      // Apply direction (except for popularity which doesn't have direction)
      if (type !== "popularity" && direction === "desc") {
        comparison = -comparison;
      }

      return comparison;
    });

    // logger.log(`✅ Sorted tracks by ${type} (${direction}):`, sorted.map(track => {
    //   const stats = getTrackWinsAndPlaces(track.name);
    //   return { name: track.name, wins: stats.wins, places: stats.places };
    // }));

    return sorted;
  };

  const handleSortChange = (selectedType) => {
    setIsDropdownOpen(false); // Close dropdown
    
    if (selectedType === sortType && selectedType !== "popularity") {
      // Toggle direction if same type is selected (except popularity)
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      logger.log(`🔁 Toggled ${selectedType} direction to: ${newDirection}`);
    } else {
      // Change to new type
      setSortType(selectedType);
      // Set default direction based on type
      const defaultDirection = selectedType === "popularity" ? "asc" : 
                              selectedType === "a-z" ? "asc" : "desc"; // winners, places, both default to desc
      setSortDirection(defaultDirection);
      logger.log(`🎯 Sort changed to: ${selectedType} (${defaultDirection})`);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.sort-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const getSortDisplayLabel = (type) => {
    const baseLabels = {
      "popularity": "Popularity",
      "a-z": "Alphabetical",
      "winners": "Wins",
      "places": "Places",
      "both": "Total"
    };
    
    return baseLabels[type] || "Sort";
  };

  const getSortArrowIcon = (type, direction, isSelected) => {
    if (type === "popularity") return null;
    
    if (isSelected) {
      return direction === "asc" ? 
        <ArrowUp className="w-3 h-3 text-[#F30099]" /> : 
        <ArrowDown className="w-3 h-3 text-[#F30099]" />;
    } else {
      return <ArrowUpDown className="w-3 h-3 opacity-50" />;
    }
  };


  return (
    <div className="home-page-container bg-white">
      <div className="w-full">
        {/* Carousel Banner */}
        <CarouselBanner />

        {/* Today's Results Section */}

        {/* Today's Tips Section */}
        {(hasRacesToday || isCarouselLoading) && (
          <div className="mt-4 mb-0">
            {/* Append to the below H2 the total sum of wins and places thus far for todays results */}
            <h2 className="text-lg font-semibold mb-2 flex items-center justify-between pt-0 pr-[1.6rem] pl-[1.1rem] shadow-[0_2px_2px_rgba(0,0,0,0.15)] pb-[12px]">
              {(isTipsLoading ||
                isLeaderboardLoading ||
                isCarouselLoading ||
                (!hasNavigated && tipsProcessedRef.current === null) ||
                (hasNavigated &&
                  tipsFilteredResults.length === 0 &&
                  (isLeaderboardLoading || tipsProcessedRef.current === null))) ? (
                <span>Tips loading</span>
              ) : shouldShowEarlyMorningMessage() && futureTodayTracks.length > 0 ? (
                <button
                  type="button"
                  onClick={toggleTodaysTipsOpen}
                  className="flex items-center gap-1 flex-1 text-left cursor-pointer"
                  aria-expanded={isTodaysTipsOpen}
                >
                  <span>Today&apos;s Tips</span>
                  {isTodaysTipsOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  )}
                </button>
              ) : shouldShowEarlyMorningMessage() || areTipsFromYesterday() ? (
                <button
                  type="button"
                  onClick={toggleTodaysTipsOpen}
                  className="flex items-center gap-1 flex-1 text-left cursor-pointer"
                  aria-expanded={isTodaysTipsOpen}
                >
                  <span>Yesterday&apos;s Tips</span>
                  {isTodaysTipsOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  )}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={toggleTodaysTipsOpen}
                  className="flex items-center gap-1 flex-1 text-left cursor-pointer"
                  aria-expanded={isTodaysTipsOpen}
                >
                  <span>Today&apos;s Tips</span>
                  {isTodaysTipsOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" aria-hidden="true" />
                  )}
                </button>
              )}
              {!(isTipsLoading || isLeaderboardLoading || isCarouselLoading) && (
                <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
                  {/* Custom Sort Dropdown */}
                  <div className="relative sort-dropdown">
                    <Button
                      onClick={toggleDropdown}
                      variant="outline"
                      size="sm"
                      className="w-[140px] h-8 px-3 py-1 text-xs justify-between bg-white border-2 border-gray-400 hover:bg-gray-50 hover:border-gray-500 shadow-sm"
                    >
                      <span>{getSortDisplayLabel(sortType)}</span>
                      <div className="flex items-center gap-1">
                        {sortType !== "popularity" && getSortArrowIcon(sortType, sortDirection, true)}
                        <ChevronDown className={`w-3 h-3 transition-transform ${isDropdownOpen ? 'rotate-180 text-[#F30099]' : 'text-gray-500'}`} />
                      </div>
                    </Button>
                    
                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-[140px] bg-white border-2 border-gray-400 rounded-md shadow-lg z-50">
                        <div className="py-1">
                          <button
                            onClick={() => handleSortChange("popularity")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "popularity" ? "bg-[#F30099]/10 text-[#F30099]" : ""
                            }`}
                          >
                            <span>Popularity</span>
                          </button>
                          <button
                            onClick={() => handleSortChange("a-z")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "a-z" ? "bg-[#F30099]/10 text-[#F30099]" : ""
                            }`}
                          >
                            <span>Alphabetical</span>
                            <div className="ml-2">
                              {getSortArrowIcon("a-z", sortDirection, sortType === "a-z")}
                            </div>
                          </button>
                          <button
                            onClick={() => handleSortChange("winners")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "winners" ? "bg-[#F30099]/10 text-[#F30099]" : ""
                            }`}
                          >
                            <span>Wins</span>
                            <div className="ml-2">
                              {getSortArrowIcon("winners", sortDirection, sortType === "winners")}
                            </div>
                          </button>
                          <button
                            onClick={() => handleSortChange("places")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "places" ? "bg-[#F30099]/10 text-[#F30099]" : ""
                            }`}
                          >
                            <span>Places</span>
                            <div className="ml-2">
                              {getSortArrowIcon("places", sortDirection, sortType === "places")}
                            </div>
                          </button>
                          <button
                            onClick={() => handleSortChange("both")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "both" ? "bg-[#F30099]/10 text-[#F30099]" : ""
                            }`}
                          >
                            <span>Total</span>
                            <div className="ml-2">
                              {getSortArrowIcon("both", sortDirection, sortType === "both")}
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <ResultsTally
                    historicalData={historicalData}
                    iconSize="w-5 h-5"
                    textSize="text-sm"
                    tipCount={2}
                  />
                </div>
              )}
            </h2>

            {/* Early morning message / preview state */}
            {isTodaysTipsOpen &&
              shouldShowEarlyMorningMessage() &&
              !(isTipsLoading || isLeaderboardLoading || isCarouselLoading) && (
                <>
                  {futureTodayTracks.length > 0 ? (
                    <div className="mt-4 mb-4 bg-[#fba6e1]/10 border border-[#fba6e1]/30 rounded-lg overflow-hidden">
                      <div className="px-4 py-3">
                        <p className="text-sm text-brand-pink text-center">
                          Yesterdays Early Mail showing until Race-Time tips generated by {raceDayTipsTime()}.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="mx-4 mt-4 mb-4 p-3 bg-brand-pink/10 border border-brand-pink rounded-lg">
                      <p className="text-sm text-brand-pink text-center">
                        Today's Tips will be available from {raceDayTipsTime()}
                      </p>
                    </div>
                  )}
                </>
              )}

            {isTodaysTipsOpen && (
              <div className="results-content-container px-0 pt-2 pb-[1rem] overflow-y-auto space-y-2">
              {/* Early-morning fallback rendering (today from future predictions) */}
              {shouldShowEarlyMorningMessage() && (
                <>
                  {isFutureTodayLoading && (
                    <div className="px-4 pb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Spinner className="w-4 h-4" />
                        <span>Loading preview tips…</span>
                      </div>
                    </div>
                  )}

                  {!isFutureTodayLoading && futureTodayTracks.length > 0 && (
                    <div
                      className="bg-[#fba6e1]/10 border border-[#fba6e1]/30 rounded-lg overflow-hidden"
                    >
                      <FutureTipsAccordion date={today} tracks={futureTodayTracks} />
                    </div>
                  )}
                </>
              )}

              {/* Skeleton Loader - Show when carousel is loading or when tips are loading */}
              {futureTodayTracks.length === 0 && (isTipsLoading || isLeaderboardLoading || isCarouselLoading || 
                (!hasNavigated && tipsProcessedRef.current === null) || 
                (hasNavigated && tipsFilteredResults.length === 0 && (isLeaderboardLoading || tipsProcessedRef.current === null))) && (
                <div className="skeleton-container px-4 py-4 space-y-4">
                  {/* Track Skeleton Items - Show 3 skeleton tracks */}
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((item) => (
                    <div key={item} className="track-skeleton bg-white rounded-lg shadow p-4">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          {/* Track name skeleton */}
                          <div className="h-5 bg-gray-200 rounded w-36 animate-pulse"></div>
                          {/* State skeleton */}
                          <div className="h-3 bg-gray-200 rounded w-10 animate-pulse"></div>
                        </div>
                        <div className="flex items-center space-x-4">
                          {/* Results tally skeleton */}
                          <div className="flex items-center space-x-2">
                            <div className="h-5 w-5 bg-gray-200 rounded-full animate-pulse"></div>
                            <div className="h-4 w-8 bg-gray-200 rounded animate-pulse"></div>
                          </div>
                          {/* Arrow skeleton */}
                          <div className="h-5 w-5 bg-gray-200 rounded animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Breadcrumbs - Only show when not loading */}
              {!isTipsLoading && tipsBreadcrumbs.length > 0 && (
                <div className="breadcrumbs-container px-4 pb-2 flex items-center justify-between w-full space-x-2 text-sm">
                  <div className="flex items-center space-x-2">
                    {tipsBreadcrumbs.map((crumb, index) => (
                      <div key={index} className="flex items-center">
                        {index > 0 && (
                          <ChevronRight className="w-4 h-4 mx-1 text-gray-400" />
                        )}
                        <span
                          className={`${crumb.path || crumb.onClick ? "text-[#F30099] cursor-pointer" : "text-gray-700"}`}
                          onClick={() =>
                            crumb.path || crumb.onClick
                              ? handleTipsBreadcrumbClick(crumb)
                              : null
                          }
                        >
                          {crumb.name}
                        </span>
                      </div>
                    ))}
                  </div>
                  {/* Add track results tally below track name */}
                  <div className="track-summary px-4 mb-2">
                    <div className="track-tally mt-1">
                      <ResultsTally
                        historicalData={historicalData}
                        trackName={selectedTipsTrack.name}
                        iconSize="w-5 h-5"
                        textSize="text-sm"
                        tipCount={2}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Race filter chips - Extra Details on, today's tracks list */}
              {showExtraDetails &&
                hasRaceCards &&
                !selectedTipsTrack &&
                tipsFilteredResults.length > 0 &&
                futureTodayTracks.length === 0 &&
                !areTipsFromYesterday() &&
                !isTipsLoading &&
                !isLeaderboardLoading && (
                  <RaceFilterChips
                    filter={activeRaceFilter}
                    onChange={setRaceFilter}
                    counts={raceFilterCounts}
                  />
                )}

              {/* Tips Content - Only show when fully loaded */}
              {futureTodayTracks.length === 0 && (!isTipsLoading && !isLeaderboardLoading && !isCarouselLoading && 
                (tipsProcessedRef.current !== null || (hasNavigated && tipsFilteredResults.length > 0))) && (
                <>
                  {tipsFilteredResults.length === 0 ? (
                    <div className="no-results-container flex flex-col items-center justify-center py-4">
                      <Frown className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-md font-medium text-gray-700">
                        No tips available yet
                      </p>
                      <p className="text-sm text-gray-500 text-center px-4">
                        Check back later for today's race tips
                      </p>
                    </div>
                  ) : selectedTipsTrack && !selectedTipsRace ? (
                    <div className="render-races-container space-y-2">
                      {selectedTipsTrack.races.map((race) => {
                        // Get race results data from historical data
                        const raceKey = `${selectedTipsTrack.name.replace(/\s+/g, "-")}-${race.number}-${format(new Date(), "dd-MM-yyyy")}`;
                        const raceResultsData = historicalData[raceKey];

                        // Check if race has completed (has podium data)
                        const isRaceCompleted =
                          raceResultsData &&
                          raceResultsData.podium &&
                          Array.isArray(raceResultsData.podium) &&
                          raceResultsData.podium.length > 0;

                        // Check for wins and places
                        let hasWins = false;
                        let hasPlaces = false;

                        if (isRaceCompleted) {
                          hasWins = raceResultsData.podium.some(
                            (result) => result === "winnerWinnerChickenDinner",
                          );
                          hasPlaces = raceResultsData.podium.some(
                            (result) => result === "PL",
                          );
                        }

                        // Check if race is abandoned
                        const isAbandoned = isRaceAbandoned(
                          selectedTipsTrack.name,
                          race.number,
                        );

                        // Get race market start time for jump time display
                        const marketStartTime = race.data?.marketStartTime
                          ? new Date(race.data.marketStartTime)
                          : null;
                        const jumpTimeStr = marketStartTime
                          ? formatTimeOfDay(marketStartTime)
                          : "";
                        const isPastJumpTime =
                          marketStartTime && marketStartTime < new Date();

                        // Build the class names
                        const raceClasses = [
                          "race-button-container",
                          "bg-white",
                          "rounded-lg",
                          "shadow",
                          "p-4",
                          "cursor-pointer",
                          "mx-4",
                          "relative", // Add relative positioning for abandoned overlay
                        ];

                        if (isRaceCompleted) raceClasses.push("race-completed");
                        if (hasWins) raceClasses.push("race-results-win");
                        if (hasPlaces) raceClasses.push("race-results-place");
                        if (isAbandoned)
                          raceClasses.push("race-abandoned bg-gray-200");

                        return (
                          <div
                            key={`${selectedTipsTrack.name}-${race.number}`}
                            className={raceClasses.join(" ")}
                            onClick={() =>
                              selectTipsRace(selectedTipsTrack, race)
                            }
                          >
                            <div className="flex justify-between items-center">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">
                                  Race {race.number}
                                </span>

                                {/* Add jump time component */}
                                {marketStartTime && (
                                  <span
                                    className={`whitespace-nowrap text-xs px-2 py-0.5 rounded ${isPastJumpTime ? "text-gray-500" : "text-deepPink"}`}
                                  >
                                    {jumpTimeStr}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center space-x-2">
                                {/* Move ResultsTally to left */}
                                {race.isTopTip && (
                                  <div className="flex items-center">
                                    <div className="bg-[#FFEBBB] rounded-full px-2 py-0.5 flex items-center">
                                      <FlameIcon className="w-4 h-4" />
                                      <span className="ml-1 text-xs text-[#A0200C] font-bold">
                                        {(() => {
                                          // Check if this race has results
                                          const raceKey = `${selectedTipsTrack.name.replace(/\s+/g, "-")}-${race.number}-${format(new Date(), "dd-MM-yyyy")}`;
                                          const raceResults = historicalData[raceKey];
                                          
                                          // If we have results and the top tip is the winner
                                          if (raceResults?.betfairResults?.winner && race.data?.firstFourData?.length > 0) {
                                            const topTipSelectionId = race.data.firstFourData[0]?.selectionId;
                                            const winnerSelectionId = raceResults.betfairResults.winner.selectionId;
                                            
                                            // If the top tip's selectionId matches the winner's selectionId
                                            if (topTipSelectionId === winnerSelectionId) {
                                              return "Hot Tip";
                                            }
                                          }
                                          
                                          return "Top Tip";
                                        })()}
                                      </span>
                                    </div>
                                  </div>
                                )}
                                <ResultsTally
                                  historicalData={historicalData}
                                  raceKey={raceKey}
                                  iconSize="w-5 h-5"
                                  textSize="text-sm"
                                  className="mr-1"
                                  tipCount={2}
                                />

                                {/* Add VIEW TIPS text */}
                                <div className="flex items-center button-view-tips text-xs font-medium text-[#F30099]">
                                  {!ResultsTally && (
                                    <span className="text-xs font-medium text-[#F30099]">
                                      VIEW <br />
                                    </span>
                                  )}

                                  <ChevronRight className="w-5 h-5 text-gray-400" />
                                </div>
                              </div>
                            </div>

                            {/* Add abandoned overlay */}
                            {isAbandoned && (
                              <div className="abandoned-indicator absolute inset-0 flex items-center justify-center font-semibold">
                                <AbandonedLabel
                                  className="text-sm px-3 py-1"
                                  reason={getAbandonmentReason(
                                    selectedTipsTrack.name,
                                  )}
                                  showReason={true}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : selectedTipsTrack && selectedTipsRace ? (
                    <div className="render-race-container">
                      <div className="space-y-2">
                        <div className="tips-master-container pt-3 pb-1 px-3 mx-2 mt-2 mb-[2px] rounded-lg shadow-[0_0_3px_rgba(0,0,0,0.25)] bg-[rgba(255,255,255,0.9)] backdrop-blur-[1.5px] transition-all duration-500">
                          {selectedTipsRace &&
                          selectedTipsRace.data &&
                          selectedTipsRace.data.firstFourData &&
                          Array.isArray(selectedTipsRace.data.firstFourData) &&
                          selectedTipsRace.data.firstFourData.length > 0 ? (
                            <RenderTips
                              leaderboardQueryData={selectedTipsRace.data}
                              leaderboardData={createLeaderboardQueryData(
                                selectedTipsRace.data.firstFourData,
                              )}
                              externalHasHistoricalData={false}
                              hasEarlyPredictions={checkHasEarlyPredictions(
                                selectedTipsTrack.name,
                                selectedTipsRace.number,
                                selectedTipsRace.date,
                                selectedTipsRace.data,
                                earlyPredictionsData,
                              )}
                              earlyPredictionsData={earlyPredictionsData}
                              selectedTrack={selectedTipsTrack.name}
                              selectedRace={selectedTipsRace.number}
                              today={format(
                                selectedTipsRace.date,
                                "dd-MM-yyyy",
                              )}
                              isChanging={false}
                              isMounted={true}
                              isTipHistoryExpanded={isTipHistoryExpanded}
                              toggleTipHistory={toggleTipHistory}
                              isResultsPage={false}
                            />
                          ) : (
                            <div className="py-8 text-center">
                              <AlertTriangle className="mx-auto h-10 w-10 text-yellow-500 mb-3" />
                              <p className="text-lg font-medium">
                                No tips data available
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                The prediction data for this race couldn't be
                                loaded
                              </p>
                              <p className="text-xs text-gray-500 mt-3">
                                {selectedTipsRace
                                  ? `Race ${selectedTipsRace.number}`
                                  : "No race selected"}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : activeRaceFilter !== "all" && !areTipsFromYesterday() ? (
                    <RaceFilterList
                      filter={activeRaceFilter}
                      races={filteredCardRaces}
                      historicalData={historicalData}
                      onSelect={selectTipsRace}
                      isRaceAbandoned={isRaceAbandoned}
                      getAbandonmentReason={getAbandonmentReason}
                    />
                  ) : (
                    <div className="render-tracks-container flex flex-col gap-2 mx-4">
                      {/* Render tracks */}
                      {(() => {
                        // Apply sorting to tracks
                        const sortedTracks = sortTracks(tipsFilteredResults, sortType, sortDirection);
                        logger.log(`📊 Applying sort: ${sortType} (${sortDirection}) to ${sortedTracks.length} tracks`);
                        
                        return (
                          <div key={`${sortType}-${sortDirection}`} className="sorted-tracks flex flex-col gap-2">
                            {sortedTracks.map((track) => {
                              // Create a key for a track
                              const trackPrefix = track.name.replace(/\s+/g, "-");

                              // Check if track has any completed races with results
                              const hasCompletedRaces = Object.keys(historicalData).some(
                                (key) =>
                                  key.startsWith(trackPrefix) &&
                                  historicalData[key].podium &&
                                  Array.isArray(historicalData[key].podium) &&
                                  historicalData[key].podium.length > 0,
                              );

                              // Check for wins and places
                              let hasWins = false;
                              let hasPlaces = false;

                              if (hasCompletedRaces) {
                                // Look through all races for this track
                                Object.keys(historicalData)
                                  .filter((key) => key.startsWith(trackPrefix))
                                  .forEach((key) => {
                                    const raceData = historicalData[key];
                                    if (raceData.podium && Array.isArray(raceData.podium)) {
                                      if (raceData.podium.some((result) => result === "winnerWinnerChickenDinner")) {
                                        hasWins = true;
                                      }
                                      if (raceData.podium.some((result) => result === "PL")) {
                                        hasPlaces = true;
                                      }
                                    }
                                  });
                              }

                              // Build the class names
                              const trackClasses = [
                                "track-button-container",
                                "bg-white",
                                "rounded-lg",
                                "shadow",
                                "mb-0",
                              ];

                              if (hasCompletedRaces) trackClasses.push("track-with-completed-races");
                              if (hasWins) trackClasses.push("track-results-win");
                              if (hasPlaces) trackClasses.push("track-results-place");

                              // Check if track is abandoned
                              const isTrackAband = isTrackAbandoned(track.name);

                              return (
                                <div
                                  key={`${sortType}-${sortDirection}-${track.name}`}
                                  className={`${trackClasses.join(" ")} ${isTrackAband ? "bg-gray-100" : ""}`}
                                >
                                  <div
                                    className="p-4 cursor-pointer"
                                    onClick={() => selectTipsTrack(track)}
                                    data-gtm-element-id={`my-tips_click_cursor-${track.name}`}
                                  >
                                    <div className="flex justify-between items-center">
                                      <div className="track-name-state-container flex items-center gap-2">
                                        {track.name && (
                                          <span className="track-name-text font-medium">
                                            {track.name}
                                          </span>
                                        )}
                                        {track.state && (
                                          <span className="text-xxs text-gray-400">
                                            {" "}
                                            ({track.state})
                                          </span>
                                        )}
                                        {isTrackAband && (
                                          <AbandonedLabel
                                            className="ml-2"
                                            reason={getAbandonmentReason(track.name)}
                                            showReason={true}
                                          />
                                        )}
                                      </div>
                                      <div className="track-badges-container flex items-center space-x-4">
                                        {track.topTips > 0 && (
                                          <div className="top-tip-container flex items-center">
                                            <div className={`${track.topTips > 1 ? "bg-[#FFEBBB] rounded-full px-2 py-0.5 flex items-center" : "flex items-center"}`}>
                                              <FlameIcon className="w-4 h-4" />
                                              {track.topTips > 1 && (
                                                <span className="ml-1 text-xs text-[#A0200C] font-bold">
                                                  x{track.topTips}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        <ResultsTally
                                          historicalData={historicalData}
                                          trackName={track.name}
                                          iconSize="w-5 h-5"
                                          textSize="text-sm"
                                          className="mr-1"
                                          tipCount={2}
                                        />
                                        <div className="button-view-tips text-xs font-medium text-[#F30099]">
                                          {!ResultsTally && (
                                            <span className="button-view-tips text-xs font-medium text-[#F30099]">
                                              VIEW
                                            </span>
                                          )}
                                          <ChevronRight className="w-5 h-5 text-gray-400" />
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </>
              )}
              </div>
            )}
          </div>
        )}

        {/* Future Tips Section - Only for paid users */}
        {isPaidUser && (
          <>
            {!isFutureTipsLoading &&
              Array.isArray(futureTipsDays) &&
              futureTipsDays.length === 0 && (
                <div className="px-4 pb-6">
                  <div className="text-sm text-gray-500">
                    No Early Mail available at the moment. Please try again after midday or tomorrow.
                  </div>
                </div>
              )}

            {futureTipsSlots.map((slot) => {
                if (slot.state === "loading") {
                  const loadingDayName = format(
                    parse(slot.date, "dd-MM-yyyy", new Date()),
                    "EEEE",
                  );
                  return (
                    <div key={`future-loading-${slot.date}`} className="px-4 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600" role="status">
                        <Spinner className="w-4 h-4" />
                        <span>Loading {loadingDayName}&apos;s Early Mail...</span>
                      </div>
                    </div>
                  );
                }
                const day = slot.day;
                const dateObj = parse(day.date, "dd-MM-yyyy", new Date());
                const dayName = format(dateObj, "EEEE");
                const header = `${dayName}'s Tips`;
                const earlyMailKey = `future-${day.date}`;
                const earlyMailPanelId = `early-mail-panel-${day.date}`;
                const isEarlyMailOpen = getEarlyMailOpen(earlyMailKey);

                return (
                  <div
                    key={`future-${day.date}`}
                    className="pt-4 pb-4 bg-[#fba6e1]/10  overflow-hidden"
                  >
                    <h2 className="mb-2">
                      <button
                        type="button"
                        aria-expanded={isEarlyMailOpen}
                        aria-controls={earlyMailPanelId}
                        onClick={() => {
                          if (!isEarlyMailOpen) setPendingScrollToId(earlyMailPanelId);
                          toggleEarlyMailOpen(earlyMailKey);
                        }}
                        className="w-full text-lg font-semibold flex items-center justify-between pt-0 pr-[1.6rem] pl-[1.1rem] shadow-[0_2px_2px_rgba(0,0,0,0.15)] pb-[12px] cursor-pointer"
                      >
                        <span className="text-left flex items-center gap-1">
                          <span>{header}</span>
                          {isEarlyMailOpen ? (
                            <ChevronDown
                              className="w-4 h-4 text-gray-400"
                              aria-hidden="true"
                            />
                          ) : (
                            <ChevronRight
                              className="w-4 h-4 text-gray-400"
                              aria-hidden="true"
                            />
                          )}
                        </span>
                        <EarlyMailPill isOpen={isEarlyMailOpen} />
                      </button>
                    </h2>

                    {isEarlyMailOpen && (
                      <div id={earlyMailPanelId} className="px-0 pb-2">
                        <FutureTipsAccordion date={day.date} tracks={day.tracks} />
                      </div>
                    )}
                  </div>
                );
              })}
          </>
        )}

        {/* No races message - Only show when not loading and confirmed no races */}
        {!hasRacesToday && !isCarouselLoading && (
          <div className="text-center py-8 px-4">
            <p className="text-gray-600 mb-2">No races available for today</p>
            <p className="text-gray-500 text-sm">
              Check back later for upcoming races
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
